import type { IUser } from "@features/users";
import { ROLE_OPERATION, ROLE_HOSPITALS, ROLE_CONVENIO } from "../../../types/role";

/**
 * Requisito de acesso a uma rota ou a um bloco de capacidade da home.
 * Mesma forma consumida por `ValidateAuth` (guard de rota) e por `useCanAccess` (home composta).
 */
export interface AccessRequirement {
  roles?: number[];
  permissions?: number[];
  /**
   * Códigos de permissão (`audit:read`…), para os direitos que nasceram sem id legado e que a lista numérica não
   * enxerga. Basta um deles, ou `admin:full`, como no `requirePermission` do backend.
   */
  permissionCodes?: string[];
  hospitalNeedsToBeActive?: boolean;
}

/** Os códigos de `user.permissoes`: o login e o `/auth/validation` mandam `codigo` em cada item (P20EE-188). */
export function extractPermissionCodes(
  user: Pick<IUser, "permissoes"> | null | undefined,
): string[] {
  return (user?.permissoes ?? [])
    .map((item: unknown) =>
      typeof item === "object" && item !== null && "codigo" in item
        ? (item as { codigo?: unknown }).codigo
        : null,
    )
    .filter((code): code is string => typeof code === "string" && code.length > 0);
}

/** Se a pessoa tem um dos códigos, ou `admin:full` (a convenção de superadmin do backend). */
export function holdsAnyPermissionCode(
  user: Pick<IUser, "permissoes"> | null | undefined,
  codes: string[],
): boolean {
  const held = extractPermissionCodes(user);
  return held.includes("admin:full") || codes.some((code) => held.includes(code));
}

/** Normaliza `user.permissoes` (que chega como `number` ou `{ id }`) para ids numéricos. */
export function extractPermissionIds(
  user: Pick<IUser, "permissoes"> | null | undefined,
): number[] {
  return (user?.permissoes ?? [])
    .map((item: unknown) => {
      if (typeof item === "number") return item;
      if (typeof item === "object" && item !== null && "id" in item) {
        return Number((item as { id: number | string }).id);
      }
      return -1;
    })
    .filter((p): p is number => p > 0);
}

export interface CanAccessOptions {
  /**
   * `true` (padrão): `ROLE_OPERATION` passa qualquer checagem de papel — é o superadmin,
   * comportamento do guard de rota (`ValidateAuth`). A home composta passa `false`, porque
   * quer a capacidade *real* da pessoa (a operação não deve ver o bloco de paciente por bypass).
   */
  superadminBypass?: boolean;
}

/**
 * Predicado único de acesso — fonte da verdade compartilhada entre o guard de rota e a home.
 * Preserva o bypass superadmin de `ROLE_OPERATION` e o escopo de permissão de
 * `ROLE_HOSPITALS`/`ROLE_CONVENIO` (+ procedência ativa só para hospital).
 */
export function computeCanAccess(
  user: Pick<IUser, "role_id" | "roles" | "permissoes"> | null | undefined,
  isProcedenciaActive: boolean | undefined,
  req: AccessRequirement,
  { superadminBypass = true }: CanAccessOptions = {},
): boolean {
  if (!user) return false;

  const roles = req.roles ?? [];
  const permissions = req.permissions ?? [];

  // O código vale para todo papel: é o direito em si, e o servidor o exige de qualquer um (P20EE-188).
  if (req.permissionCodes?.length && !holdsAnyPermissionCode(user, req.permissionCodes)) {
    return false;
  }

  // The token's active role always counts, with the linked ones. With unified login the backend builds `roles` from
  // the legacy accounts linked to the person, and a person who exists only in the core has none: `roles` came back
  // empty for the seeded doctor (role_id 4) on dev on 2026-10-15, and `roles ?? [role_id]` denied every doctor route.
  const userRoles = [...(user.roles ?? []), ...(user.role_id != null ? [user.role_id] : [])];
  const hasRole = roles.length === 0 || roles.some((r) => userRoles.includes(r));

  if (!hasRole && !(superadminBypass && user.role_id === ROLE_OPERATION)) {
    return false;
  }

  // Permissões e procedência são escopadas a hospital/convênio (idêntico ao ValidateAuth).
  if (user.role_id === ROLE_HOSPITALS || user.role_id === ROLE_CONVENIO) {
    const userPermissions = extractPermissionIds(user);
    const hasPermission =
      permissions.length === 0 ||
      permissions.some((permission) => userPermissions.includes(permission));
    if (!hasPermission) return false;

    if (user.role_id === ROLE_HOSPITALS) {
      if (isProcedenciaActive === false && req.hospitalNeedsToBeActive) return false;
    }
  }

  return true;
}
